import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { Provider } from '@ffd/api-client';
import { SelectProviderComponent } from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { NavigationStart, Router } from '@angular/router';
import { filter, first } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectCurrentUserProfile } from '@libs/store';
import {
  PerioExamSettingsModalComponent,
  PerioExamSettingsModalData,
  PerioExamSettingsModalPayload,
} from '../perio-exam-settings-modal/perio-exam-settings-modal.component';
import { PerioExamScreenService } from '../perio-exam-screen/services/perio-exam/perio-exam-screen.service';
import { PerioExamRecordingsModalComponent } from '../perio-exam-recordings-modal/perio-exam-recordings-modal.component';

export interface PerioExamHeaderMoreModalResult {
  onResetClicked?: boolean;
}

@Component({
  selector: 'lib-perio-exam-header-more-modal',
  imports: [CommonModule, SvgIconComponent, SelectProviderComponent, TranslateModule],
  templateUrl: './perio-exam-header-more-modal.component.html',
  styleUrl: './perio-exam-header-more-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerioExamHeaderMoreModalComponent implements OnInit {
  private readonly perioExamScreenService = inject(PerioExamScreenService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  protected provider = computed(() => this.perioExamScreenService.viewModel().provider);
  protected extractedTeethPositions = computed(() => this.perioExamScreenService.viewModel().extractedTeethPositions);
  protected missingTeethPositions = computed(() => this.perioExamScreenService.viewModel().missingTeethPositions);
  private readonly dialog = inject(Dialog);
  private readonly dialogRef = inject(DialogRef);

  protected readonly userProfile = this.store.selectSignal(selectCurrentUserProfile);
  protected readonly isGlobalAdmin = computed(() => this.userProfile()?.isGlobalAdmin ?? false);

  public ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        first(),
      )
      .subscribe(() => {
        this.perioExamSettingsModal?.close();
        this.perioExamRecordingsModal?.close();
        this.dialogRef.close();
      });
  }

  private perioExamSettingsModal: DialogRef<PerioExamSettingsModalPayload> | null = null;
  private perioExamRecordingsModal: DialogRef<unknown, PerioExamRecordingsModalComponent> | null = null;

  protected onPreferencesButtonClick(): void {
    const missingTeeth = this.missingTeethPositions();
    const extractedTeeth = this.extractedTeethPositions();
    this.perioExamSettingsModal = this.dialog.open<PerioExamSettingsModalPayload, PerioExamSettingsModalData>(
      PerioExamSettingsModalComponent,
      {
        data: { missingTeeth, extractedTeeth },
        backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
        panelClass: ['[&>*]:outline-none'],
        disableClose: true,
      },
    );

    this.perioExamSettingsModal.closed.subscribe(() => {
      this.perioExamSettingsModal = null;
    });

    this.dialogRef.close();
  }

  protected onResetButtonClick(): void {
    this.dialogRef.close({ onResetClicked: true });
  }

  protected onProviderChange(provider: Provider): void {
    this.perioExamScreenService.updatePerioExamProvider(provider);
  }

  protected onRecordingsButtonClick(): void {
    const modalRef = this.dialog.open(PerioExamRecordingsModalComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['[&>*]:outline-none'],
    });

    modalRef.closed.subscribe(() => {
      this.perioExamRecordingsModal = null;
    });

    this.perioExamRecordingsModal = modalRef;
    this.dialogRef.close();
  }
}
